import React, { useState } from 'react';
import {
  Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Alert, CircularProgress,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableChart as CsvIcon,
  GridOn as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../contexts/AuthContext';
import { downloadCsv, downloadExcel, printPdf, StatsExport as ExportData } from '../utils/statsExport';

const API_URL = process.env.REACT_APP_API_URL || '';

type Format = 'csv' | 'excel' | 'pdf';

const iso = (d: Date) => {
  // Local date, not UTC: toISOString() on a local midnight can land on the previous day.
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

/**
 * Export the Stats view as CSV, Excel or PDF, over a preset or custom date range.
 *
 * The range lives here rather than on the page's own 7/30/90 toggle: exporting a specific
 * month for a report is a different job from browsing the last month, and tying them together
 * would mean changing what is on screen just to get the file you want.
 */
export const StatsExport: React.FC = () => {
  const { token } = useAuth();
  const theme = useTheme();
  // A date-range dialog is cramped on a phone; full-screen gives the pickers room.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [preset, setPreset] = useState<number | 'custom'>(30);
  const [from, setFrom] = useState<Date | null>(new Date(Date.now() - 30 * 86400000));
  const [to, setTo] = useState<Date | null>(new Date());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<Format | null>(null);

  const query = () => {
    if (preset === 'custom' && from && to) return `from=${iso(from)}&to=${iso(to)}`;
    return `days=${preset === 'custom' ? 30 : preset}`;
  };

  const run = async (format: Format) => {
    if (!token) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/stats/export?${query()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not build the export');
      const data: ExportData = await res.json();

      if (format === 'csv') downloadCsv(data);
      else if (format === 'excel') downloadExcel(data);
      else if (!printPdf(data)) {
        setError('The print window was blocked. Allow pop-ups for this site and try again.');
        return;
      }
      setRangeOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the export');
    } finally {
      setBusy(false);
    }
  };

  const choose = (format: Format) => {
    setAnchor(null);
    setPending(format);
    setRangeOpen(true);
  };

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="primary"
        disableElevation
        startIcon={<DownloadIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Export
      </Button>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => choose('csv')}>
          <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="CSV" secondary="Opens anywhere" />
        </MenuItem>
        <MenuItem onClick={() => choose('excel')}>
          <ListItemIcon><ExcelIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Excel" secondary="One sheet per section" />
        </MenuItem>
        <MenuItem onClick={() => choose('pdf')}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="PDF" secondary="Opens your print dialog" />
        </MenuItem>
      </Menu>

      <Dialog
        open={rangeOpen}
        onClose={() => !busy && setRangeOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={narrow}
      >
        <DialogTitle>
          Export as {pending === 'csv' ? 'CSV' : pending === 'excel' ? 'Excel' : 'PDF'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Which period should the file cover?
          </Typography>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, v) => v && setPreset(v)}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <ToggleButton value={7}>7 days</ToggleButton>
            <ToggleButton value={30}>30 days</ToggleButton>
            <ToggleButton value={90}>90 days</ToggleButton>
            <ToggleButton value={365}>1 year</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          {preset === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 1 }}>
                <DatePicker
                  label="From"
                  value={from}
                  onChange={setFrom}
                  maxDate={to || undefined}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="To"
                  value={to}
                  onChange={setTo}
                  minDate={from || undefined}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          )}

          {pending === 'pdf' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This opens a print-ready page and your browser's print dialog — choose
              <strong> Save as PDF</strong> as the destination.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRangeOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => pending && run(pending)}
            disabled={busy || (preset === 'custom' && (!from || !to))}
            startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />}
          >
            {busy ? 'Building…' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatsExport;
